import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import VisitorAvatar from './VisitorAvatar';
import { LIMITS, fmtPhone } from '../visitorConstants';

// Blocking is advisory at this layer — the record is flagged and an activity
// log entry is written, but enforcement happens when a visit is created. The
// copy says so, because "Blocked" on its own reads like the gate is sealed.
//
// `reason` is optional on the API, so the confirm button is never gated on it.
const BlockVisitorModal = ({ visitor, submitting = false, onCancel, onConfirm }) => {
    const [reason, setReason] = useState('');

    // A fresh reason per visitor — carrying the previous one over would quietly
    // attach the wrong note to the wrong person.
    useEffect(() => { setReason(''); }, [visitor?.visitor_id]);

    if (!visitor) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-start tw-gap-3">
                    <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                        <Icon icon="mdi:block-helper" className="tw-text-lg" />
                    </div>
                    <div>
                        <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800">Block this visitor?</h3>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Security will be warned when this visitor is checked in.
                        </p>
                    </div>
                </div>

                <div className="tw-p-5 tw-space-y-4">
                    <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100">
                        <VisitorAvatar visitor={visitor} size={38} />
                        <div className="tw-min-w-0">
                            <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate">{visitor.name}</p>
                            <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-font-mono">{fmtPhone(visitor.phone)}</p>
                        </div>
                    </div>

                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                            Reason
                            <span className="tw-font-normal tw-text-gray-400"> — optional, but it is what the next person on duty will read</span>
                        </label>
                        <textarea
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            maxLength={LIMITS.blocked_reason}
                            placeholder="e.g. Misbehaviour at gate on 12 Sept"
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500 tw-resize-none"
                        />
                        <p className="tw-mb-0 tw-text-[10px] tw-text-gray-400 tw-mt-1">{reason.length}/{LIMITS.blocked_reason}</p>
                    </div>

                    <p className="tw-mb-0 tw-flex tw-items-start tw-gap-1.5 tw-text-[10px] tw-text-gray-500">
                        <Icon icon="mdi:information-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                        You can unblock at any time. The visitor&apos;s history and visit count stay intact.
                    </p>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => onConfirm(reason.trim())}
                            disabled={submitting}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:block-helper" className="tw-text-sm" />
                            {submitting ? 'Blocking...' : 'Block visitor'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlockVisitorModal;
